import { BaseEdgeFunctionClient } from '../shared/base-client';
import { 
  AgentRequest, 
  AgentResponse, 
  AgentMessage,
  OpenAIConfig 
} from './types';

export class OpenAIAgentsClient extends BaseEdgeFunctionClient {
  constructor(config: OpenAIConfig = {}) {
    super({
      ...config,
      retries: 0  // CRITICAL: Disable retries for OpenAI agents - each call creates a new run
    });
  }

  /**
   * Send a message to an AI agent
   */
  async sendMessage(
    message: string,
    threadId: string,
    assistantId: string,
    messageHistory?: AgentMessage[],
    openaiThreadId?: string // Optional OpenAI thread ID for direct API calls
  ): Promise<AgentResponse> {
    try {
      const request: AgentRequest = {
        message,
        threadId,
        openaiThreadId, // Pass OpenAI thread ID if provided
        assistantId,
        messageHistory,
        appName: 'AI Agents',
        functionName: 'Chat Conversation'
      };

      const response = await this.invokeEdgeFunction<AgentResponse>(
        'agents',
        request
      );

      return response;
    } catch (error) {
      this.handleError(error, 'Failed to send message to agent');
    }
  }

  /**
   * Send a message with full request object
   */
  async sendMessageWithRequest(request: AgentRequest): Promise<AgentResponse> {
    try {
      const response = await this.invokeEdgeFunction<AgentResponse>(
        'agents',
        request
      );

      return response;
    } catch (error) {
      this.handleError(error, 'Failed to send message to agent');
    }
  }

  /**
   * Continue a conversation in an existing thread
   */
  async continueConversation(
    message: string,
    threadId: string,
    assistantId: string,
    previousMessages: AgentMessage[] = []
  ): Promise<AgentResponse> {
    return this.sendMessage(message, threadId, assistantId, previousMessages);
  }

  /**
   * Send a message with context from previous conversation
   */
  async sendMessageWithContext(
    message: string,
    threadId: string,
    assistantId: string,
    context: {
      messageHistory?: AgentMessage[];
      systemPrompt?: string;
      metadata?: Record<string, any>;
    } = {}
  ): Promise<AgentResponse> {
    const { messageHistory, systemPrompt, metadata } = context;
    
    // Add system prompt as first message if provided
    const messages = systemPrompt 
      ? [{ role: 'system' as const, content: systemPrompt, metadata }, ...(messageHistory || [])]
      : messageHistory;

    return this.sendMessage(message, threadId, assistantId, messages);
  }
} 